import { isMobile, parseConfig } from '@utilities/theme';

/**
 * @fileoverview ProductMedia Web Component for product image gallery and zoom functionality
 * Handles gallery display, scroll effects, lazy loading, and zoom modals.
 * Uses native <dialog> element for proper accessibility (focus trapping, Escape key, etc.)
 * @version 2.0.1
 */

// Shared DOMParser instance - reused across all media parsing
const domParser = new DOMParser();

if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();
      this.isDesktopSlider = this.classList.contains('product__slider-slider-grid');
      this.slides = this.querySelectorAll('.product__slide');
      this.isNav = this.dataset.type == 'nav';
      this.cellSelector = this.isNav ? 'js-thumb-slide' : 'js-main-slide';
      this.style.display = 'flex';

      // Track current media URL to prevent unnecessary updates
      this.currentMediaUrl = null;
      this.isUpdating = false;

      // Lazy loading support
      this.lazySlides = new Set();
      this.observer = null;

      // Zoom functionality state
      /** @type {Array<Object>} Parsed media data with src, thumbnail, and zoom URLs */
      this.mediaItems = [];

      /** @type {number} Current active image index for zoom */
      this.currentZoomIndex = 0;

      /** @type {string} Product title for modal display */
      this.productTitle = '';

      /** @type {number} Width for zoom images */
      this.zoomWidth = 2048;

      /** @type {AbortController} For cleaning up event listeners */
      this.abortController = new AbortController();

      /** @type {HTMLDialogElement|null} Reference to active dialog for cleanup */
      this.activeModal = null;

      /** @type {HTMLElement|null} Element that triggered the modal (for focus return) */
      this.triggerElement = null;

      // Immediately add js-main-slide class to prevent layout shift
      this.initializeSlides();
    }

    static get observedAttributes() {
      return ['data-media-url'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-media-url' && oldValue !== newValue && newValue) {
        this.updateMedia(newValue);
      }
    }

    initializeSlides() {
      // Add the js-main-slide class immediately to prevent slides from being hidden initially
      this.slides.forEach((slide, index) => {
        if (!this.isNav) {
          slide.classList.add('js-main-slide');
          if (index === 0) {
            slide.classList.add('first-main-slide');
          }
        } else {
          slide.classList.add('js-thumb-slide');
        }
      });
    }

    connectedCallback() {
      this.initScrollEffect();

      // Check for initial media URL
      const initialMediaUrl = this.dataset.mediaUrl;
      if (initialMediaUrl) {
        this.currentMediaUrl = initialMediaUrl;
      }

      // Initialize lazy loading for slides
      if (this.dataset.lazyLoad === 'true') {
        this.initLazyLoading();
      }

      // Initialize zoom functionality
      this.#initializeZoom();
      this.#setupZoomEventListeners();
    }

    /**
     * Initialize zoom with config data
     * @private
     */
    #initializeZoom() {
      // Get configuration from data attributes
      this.productTitle = this.dataset.productTitle || '';
      this.zoomWidth = parseInt(this.dataset.zoomWidth, 10) || 2048;

      // Parse initial media from inline JSON config
      const config = parseConfig(this);
      if (config?.media) {
        this.setZoomMedia(config.media);
      }
    }

    /**
     * Set up zoom-related event listeners
     * @private
     */
    #setupZoomEventListeners() {
      const { signal } = this.abortController;

      // Listen for clicks on zoom trigger elements within this component
      this.addEventListener('click', this.#handleZoomTriggerClick.bind(this), { signal });
      this.addEventListener('keydown', this.#handleZoomTriggerKeydown.bind(this), { signal });

      // Listen for product-overview initialization to get initial media
      document.addEventListener('product-overview:initialized', this.#handleProductInitialized.bind(this), { signal });

      // Listen for variant changes to update media
      document.addEventListener('product-overview:swatch-change', this.#handleVariantChange.bind(this), { signal });

      // Try to get initial media from already-initialized product-overview
      this.#tryGetInitialMedia();
    }

    /**
     * Handle click events on zoom trigger elements
     * @private
     * @param {MouseEvent} event
     */
    #handleZoomTriggerClick(event) {
      const trigger = event.target.closest('[data-zoom-trigger]');
      if (!trigger) return;

      // Don't trigger if clicking on interactive elements within the trigger
      if (event.target.closest('button, a, input')) return;

      this.#openZoomFromTrigger(trigger);
    }

    /**
     * Handle keyboard events on zoom trigger elements
     * @private
     * @param {KeyboardEvent} event
     */
    #handleZoomTriggerKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const trigger = event.target.closest('[data-zoom-trigger]');
      if (!trigger) return;

      event.preventDefault();
      this.#openZoomFromTrigger(trigger);
    }

    /**
     * Open zoom modal from a trigger element
     * @private
     * @param {HTMLElement} trigger - Element with data-zoom-trigger attribute
     */
    #openZoomFromTrigger(trigger) {
      const index = parseInt(trigger.dataset.mediaIndex, 10) || 0;
      const productTitle = trigger.dataset.productTitle || this.productTitle;

      if (productTitle) {
        this.productTitle = productTitle;
      }

      this.openZoom(index);
    }

    /**
     * Try to get initial media from an already-initialized product-overview
     * @private
     */
    #tryGetInitialMedia() {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const productOverview = this.closest('product-overview');
        if (productOverview?.classList.contains('product-overview--initialized')) {
          const variant = productOverview.getVariant?.();
          if (variant?.media && Array.isArray(variant.media)) {
            this.setZoomMedia(variant.media);
          }
        }
      });
    }

    /**
     * Handle product-overview:initialized event to get initial media
     * @private
     * @param {CustomEvent} event - product-overview:initialized event
     */
    #handleProductInitialized(event) {
      const { variant } = event.detail || {};

      if (variant?.media && Array.isArray(variant.media)) {
        this.setZoomMedia(variant.media);
      }
    }

    /**
     * Handle variant change event to update media
     * @private
     * @param {CustomEvent} event - product-overview:swatch-change event
     */
    #handleVariantChange(event) {
      const { variant } = event.detail || {};

      if (variant?.media && Array.isArray(variant.media)) {
        this.setZoomMedia(variant.media);
      }
    }

    /**
     * Set media items for zoom from variant.media array
     * @param {Array<string>} mediaHtmlArray - Array of img HTML strings from variant.media
     */
    setZoomMedia(mediaHtmlArray) {
      if (!Array.isArray(mediaHtmlArray)) {
        console.warn('ProductMedia: setZoomMedia requires an array of HTML strings');
        return;
      }

      this.mediaItems = mediaHtmlArray.map((html, index) => this.#parseMediaHtml(html, index));
    }

    /**
     * Parse srcset attribute into sorted array of {url, width} objects
     * @private
     * @param {string} srcset - srcset attribute value
     * @returns {Array<{url: string, width: number}>} Parsed and sorted sources (descending by width)
     */
    #parseSrcset(srcset) {
      if (!srcset) return [];

      return srcset.split(',')
        .map(source => {
          const [url, descriptor] = source.trim().split(/\s+/);
          return { url, width: parseInt(descriptor, 10) || 0 };
        })
        .sort((a, b) => b.width - a.width); // Sort descending by width
    }

    /**
     * Parse an img HTML string to extract image URLs
     * @private
     * @param {string} html - HTML string containing img tag
     * @param {number} index - Index of this media item
     * @returns {Object} Parsed media object with urls
     */
    #parseMediaHtml(html, index) {
      const doc = domParser.parseFromString(html, 'text/html');
      const img = doc.querySelector('img');

      if (!img) {
        console.warn(`ProductMedia: Could not parse img from HTML at index ${index}`);
        return { index, src: '', thumbnail: '', zoom: '', mobileZoom: '', alt: '' };
      }

      const src = img.getAttribute('src') || '';
      const alt = img.getAttribute('alt') || '';
      const sources = this.#parseSrcset(img.getAttribute('srcset') || '');

      // Get zoom URLs from parsed sources
      const zoomSrc = this.#getZoomFromSources(sources, src, this.zoomWidth);
      const mobileZoomSrc = this.#getMobileZoomFromSources(sources, src);
      const thumbnailSrc = this.#adjustImageWidth(src, 300);

      return { index, src, thumbnail: thumbnailSrc, zoom: zoomSrc, mobileZoom: mobileZoomSrc, alt };
    }

    /**
     * Get highest resolution image from parsed sources
     * @private
     * @param {Array} sources - Parsed srcset sources (sorted descending)
     * @param {string} fallback - Fallback src
     * @param {number} targetWidth - Target width for fallback adjustment
     * @returns {string} URL of highest resolution image
     */
    #getZoomFromSources(sources, fallback, targetWidth) {
      if (sources.length > 0 && sources[0].url) {
        return sources[0].url; // Already sorted descending, first is highest
      }
      return this.#adjustImageWidth(fallback, targetWidth);
    }

    /**
     * Get mobile-appropriate zoom image from parsed sources
     * @private
     * @param {Array} sources - Parsed srcset sources (sorted descending)
     * @param {string} fallback - Fallback src
     * @returns {string} URL of mobile zoom image
     */
    #getMobileZoomFromSources(sources, fallback) {
      const mobileBreakpoint = window.theme?.breakpoints?.mobile || 767;
      const mobileMinWidth = 800;

      if (sources.length === 0) {
        return this.#adjustImageWidth(fallback, mobileMinWidth);
      }

      // Sources are sorted descending, find smallest that's > breakpoint
      // Reverse iterate to find first (smallest) above breakpoint
      for (let i = sources.length - 1; i >= 0; i--) {
        if (sources[i].width > mobileBreakpoint && sources[i].url) {
          return sources[i].url;
        }
      }

      // Fallback to largest available
      if (sources[0].url) {
        return sources[0].url;
      }

      return this.#adjustImageWidth(fallback, mobileMinWidth);
    }

    /**
     * Adjust image URL width parameter
     * @private
     * @param {string} url - Image URL
     * @param {number} width - Desired width
     * @returns {string} URL with adjusted width
     */
    #adjustImageWidth(url, width) {
      if (!url) return url;

      // Handle Shopify CDN URLs with width parameter
      // Pattern: ...width=XXX or ...&width=XXX
      if (url.includes('width=')) {
        return url.replace(/width=\d+/, `width=${width}`);
      }

      // If URL has query params, append width
      if (url.includes('?')) {
        return `${url}&width=${width}`;
      }

      return `${url}?width=${width}`;
    }

    /**
     * Open the zoom modal at a specific index
     * @param {number} index - Image index to open at
     */
    openZoom(index = 0) {
      if (this.mediaItems.length === 0) {
        console.warn('ProductMedia: No media items to display');
        return;
      }

      this.currentZoomIndex = Math.max(0, Math.min(index, this.mediaItems.length - 1));

      try {
        if (!isMobile()) {
          this.#openDesktopModal();
        } else {
          this.#openMobileModal();
        }

        this.dispatchEvent(new CustomEvent('product-media:zoom-opened', {
          bubbles: true,
          detail: { index: this.currentZoomIndex }
        }));
      } catch (error) {
        console.error('ProductMedia: Error opening zoom modal', error);
      }
    }

    /**
     * Close the zoom modal
     * Uses native dialog.close() which triggers the 'close' event
     */
    closeZoom() {
      if (this.activeModal && this.activeModal.open) {
        this.activeModal.close();
      }
    }

    /**
     * Handle dialog close event (fires from close(), cancel, or Escape key)
     * Guards against double execution since both cancel and close events may fire
     * @private
     */
    #onDialogClose() {
      // Guard against double execution (cancel + close events)
      if (!this.activeModal) {
        return;
      }

      // Unlock body scroll
      window.scrollLock?.disableBodyScrollLock();

      // Store reference before nullifying
      const modal = this.activeModal;
      const trigger = this.triggerElement;

      // Clean up references
      this.activeModal = null;
      this.triggerElement = null;

      // Remove modal from DOM
      modal.remove();

      // Return focus to trigger element
      if (trigger) {
        trigger.focus();
      }

      this.dispatchEvent(new CustomEvent('product-media:zoom-closed', {
        bubbles: true,
        detail: { index: this.currentZoomIndex }
      }));
    }

    /**
     * Clone template content as DocumentFragment
     * @private
     * @param {string} templateId - Template element ID
     * @returns {DocumentFragment|null}
     */
    #cloneTemplate(templateId) {
      try {
        const template = document.getElementById(templateId);
        if (!template || !(template instanceof HTMLTemplateElement)) {
          console.warn(`ProductMedia: Template not found: ${templateId}`);
          return null;
        }
        return template.content.cloneNode(true);
      } catch (error) {
        console.error(`ProductMedia: Error cloning template ${templateId}`, error);
        return null;
      }
    }

    /**
     * Open desktop modal with thumbnails
     * Uses native <dialog> element with showModal() for automatic:
     * - Focus trapping within the dialog
     * - Escape key handling
     * - Background inert (content not interactive)
     * - Proper accessibility semantics
     * @private
     */
    #openDesktopModal() {
      const modalFragment = this.#cloneTemplate('zoom-container-desktop-template');
      if (!modalFragment) {
        console.error('ProductMedia: Desktop template not found');
        return;
      }

      const currentMedia = this.mediaItems[this.currentZoomIndex];

      // Populate template slots
      const zoomedPanel = modalFragment.querySelector('[data-slot="zoomed-panel"]');
      const titleSlot = modalFragment.querySelector('[data-slot="title"]');
      const imageSlot = modalFragment.querySelector('[data-slot="image"]');
      const thumbnailsSlot = modalFragment.querySelector('[data-slot="thumbnails"]');
      const closeSlot = modalFragment.querySelector('[data-slot="close"]');

      if (zoomedPanel && currentMedia.zoom) {
        zoomedPanel.style.backgroundImage = `url('${currentMedia.zoom}')`;
      }

      if (titleSlot) {
        titleSlot.textContent = this.productTitle;
      }

      if (imageSlot && currentMedia.zoom) {
        imageSlot.src = currentMedia.zoom;
        imageSlot.alt = currentMedia.alt || this.productTitle;
      }

      if (thumbnailsSlot) {
        const thumbnailsFragment = this.#buildThumbnails();
        thumbnailsSlot.appendChild(thumbnailsFragment);
      }

      if (closeSlot && window.theme?.elements?.svg_close) {
        closeSlot.innerHTML = window.theme.elements.svg_close;
      }

      // Store trigger element for focus return
      this.triggerElement = document.activeElement;

      // Create native dialog element
      const dialog = document.createElement('dialog');
      dialog.classList.add('zoom-container--desktop');
      dialog.setAttribute('aria-label', `${this.productTitle} - Zoom view`);
      dialog.appendChild(modalFragment);

      // Store abort controller signal for cleanup
      const { signal } = this.abortController;

      // Handle native close event (dialog.close())
      dialog.addEventListener('close', () => this.#onDialogClose(), { signal });

      // Handle Escape key explicitly - more reliable than native cancel event
      // which can be interfered with by document-level keydown listeners
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeZoom();
        }
      }, { signal });

      // Handle backdrop click (clicking outside content)
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          this.closeZoom();
        }
      }, { signal });

      document.body.appendChild(dialog);
      this.activeModal = dialog;

      // Show as modal - this enables:
      // - Focus trapping (Tab cycles within dialog)
      // - Escape key closes dialog
      // - Background content becomes inert
      // - ::backdrop pseudo-element for overlay
      dialog.showModal();

      // Lock body scroll to prevent scrolling behind the dialog
      window.scrollLock?.enabledBodyScrollLock();

      // Setup modal functionality (zoom, thumbnails, close button)
      this.#setupModalInteractions(dialog);

      // Preload adjacent images for smoother initial navigation
      this.#preloadAdjacentZoomImages(this.currentZoomIndex);

      // Move focus to close button
      dialog.querySelector('.product-media-modal-desktop__toggle')?.focus();
    }

    /**
     * Build thumbnails as DocumentFragment
     * @private
     * @returns {DocumentFragment}
     */
    #buildThumbnails() {
      const fragment = document.createDocumentFragment();

      this.mediaItems.forEach((media, index) => {
        const thumbnailFragment = this.#cloneTemplate('zoom-container-desktop-thumbnail-template');
        if (!thumbnailFragment) return;

        const img = thumbnailFragment.querySelector('[data-slot="thumbnail-image"]');
        if (img) {
          img.id = `thumbnail-${index}`;
          img.src = media.thumbnail;
          img.alt = media.alt || `${this.productTitle} - Image ${index + 1}`;
          img.setAttribute('zoomed-image', media.zoom);

          if (index === this.currentZoomIndex) {
            img.classList.add('thumbnail-active');
          }
        }

        fragment.appendChild(thumbnailFragment);
      });

      return fragment;
    }

    /**
     * Setup all modal interactions
     * @private
     * @param {HTMLDialogElement} dialog
     */
    #setupModalInteractions(dialog) {
      const zoomedPanel = dialog.querySelector('.right-panel-zoomed');
      const thumbnails = dialog.querySelectorAll('.image-thumbnail');
      const closeButton = dialog.querySelector('.product-media-modal-desktop__toggle');

      if (zoomedPanel) {
        this.#setupImageZoom(zoomedPanel);
      }

      if (thumbnails.length > 0) {
        this.#setupThumbnailClick(zoomedPanel, thumbnails, dialog);
      }

      if (thumbnails.length > 4) {
        this.#setupThumbnailScrolling(dialog);
      }

      this.#setupModalClose(closeButton);
    }

    /**
     * Setup mouse-based zoom functionality
     * @private
     * @param {HTMLElement} zoomedObject
     */
    #setupImageZoom(zoomedObject) {
      if (!zoomedObject) return;

      try {
        const img = zoomedObject.querySelector('img');
        if (!img) return;

        // Center image initially
        zoomedObject.scrollLeft = (img.width - zoomedObject.clientWidth) / 2;
        zoomedObject.scrollTop = (img.height - zoomedObject.clientHeight) / 2;

        const mouseMoveHandler = (event) => {
          try {
            const offsetX = event.offsetX;
            const offsetY = event.offsetY;
            const x = (offsetX / zoomedObject.offsetWidth) * 100;
            const y = (offsetY / zoomedObject.offsetHeight) * 100;
            zoomedObject.style.backgroundPosition = `${x}% ${y}%`;
            zoomedObject.classList.add('is-zoomed');
          } catch (error) {
            console.warn('ProductMedia: Error in mousemove handler', error);
          }
        };

        const { signal } = this.abortController;
        img.addEventListener('mousemove', mouseMoveHandler, { signal });
        img.addEventListener('mouseover', () => {
          zoomedObject.classList.add('is-zoomed');
        }, { signal });
        img.addEventListener('mouseout', () => {
          zoomedObject.classList.remove('is-zoomed');
        }, { signal });
      } catch (error) {
        console.error('ProductMedia: Error setting up image zoom', error);
      }
    }

    /**
     * Setup thumbnail click handlers and keyboard navigation
     * Note: Thumbnails are now <button> elements, so they're natively focusable
     * @private
     * @param {HTMLElement} zoomedObject
     * @param {NodeListOf<HTMLElement>} thumbnails
     * @param {HTMLDialogElement} dialog
     */
    #setupThumbnailClick(zoomedObject, thumbnails, dialog) {
      if (!zoomedObject || !thumbnails?.length) return;

      const { signal } = this.abortController;

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          this.#switchToThumbnail(thumbnail, thumbnails, zoomedObject, dialog);
        }, { signal });
      });

      this.#setupKeyboardNavigation(thumbnails, zoomedObject, dialog);
    }

    /**
     * Switch to a specific thumbnail
     * @private
     * @param {HTMLElement} selectedThumbnail
     * @param {NodeListOf<HTMLElement>} allThumbnails
     * @param {HTMLElement} zoomedObject
     * @param {HTMLDialogElement} dialog
     */
    #switchToThumbnail(selectedThumbnail, allThumbnails, zoomedObject, dialog) {
      const currentActive = dialog.querySelector('.thumbnail-active');
      currentActive?.classList.remove('thumbnail-active');

      const newImageSrc = selectedThumbnail.querySelector('img')?.getAttribute('zoomed-image');
      if (!newImageSrc) return;

      const zoomImg = zoomedObject?.querySelector('img');
      if (zoomImg) {
        zoomImg.src = newImageSrc;
        zoomedObject.style.backgroundImage = `url('${newImageSrc}')`;
      }

      const thumbnailImg = selectedThumbnail.querySelector('img');
      if (thumbnailImg) {
        thumbnailImg.classList.add('thumbnail-active');

        const newIndex = Array.from(allThumbnails).indexOf(selectedThumbnail);
        if (newIndex !== -1) {
          this.currentZoomIndex = newIndex;
          // Preload adjacent images for smoother navigation
          this.#preloadAdjacentZoomImages(newIndex);
        }
      }
    }

    /**
     * Preload adjacent zoom images for smoother navigation
     * Preloads the next 2 images and 1 previous image from the current index
     * @private
     * @param {number} currentIndex - The current image index
     */
    #preloadAdjacentZoomImages(currentIndex) {
      if (!this.mediaItems?.length) return;

      // Collect adjacent indices: 1 previous, 2 next
      const indicesToPreload = [];

      // Previous image
      if (currentIndex > 0) {
        indicesToPreload.push(currentIndex - 1);
      }

      // Next 2 images
      for (let i = 1; i <= 2; i++) {
        const nextIndex = currentIndex + i;
        if (nextIndex < this.mediaItems.length) {
          indicesToPreload.push(nextIndex);
        }
      }

      // Preload each image
      indicesToPreload.forEach(index => {
        const media = this.mediaItems[index];
        if (media?.zoom) {
          const preloadImg = new Image();
          preloadImg.src = media.zoom;
        }
      });

      if (this.allowDebug()) {
        console.log(`🖼️ Product Media: Preloading zoom images at indices: ${indicesToPreload.join(', ')}`);
      }
    }

    /**
     * Setup keyboard navigation
     * @private
     * @param {NodeListOf<HTMLElement>} thumbnails
     * @param {HTMLElement} zoomedObject
     * @param {HTMLDialogElement} dialog
     */
    #setupKeyboardNavigation(thumbnails, zoomedObject, dialog) {
      const keyHandler = (event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

        event.preventDefault();

        const activeThumbnail = dialog.querySelector('.thumbnail-active');
        if (!activeThumbnail) return;

        const currentIndex = Array.from(thumbnails).findIndex(thumb =>
          thumb.querySelector('img') === activeThumbnail
        );

        let newIndex = currentIndex;
        if (event.key === 'ArrowUp' && currentIndex > 0) {
          newIndex = currentIndex - 1;
        } else if (event.key === 'ArrowDown' && currentIndex < thumbnails.length - 1) {
          newIndex = currentIndex + 1;
        }

        if (newIndex !== currentIndex) {
          this.#switchToThumbnail(thumbnails[newIndex], thumbnails, zoomedObject, dialog);
          this.#scrollThumbnailIntoView(thumbnails[newIndex], dialog);
        }
      };

      document.addEventListener('keydown', keyHandler, { signal: this.abortController.signal });
    }

    /**
     * Scroll thumbnail into view
     * @private
     * @param {HTMLElement} thumbnail
     * @param {HTMLDialogElement} dialog
     */
    #scrollThumbnailIntoView(thumbnail, dialog) {
      const container = dialog.querySelector('.thumbnails-image-box');
      if (!container || !thumbnail) return;

      const thumbnailRect = thumbnail.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      if (thumbnailRect.top < containerRect.top || thumbnailRect.bottom > containerRect.bottom) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /**
     * Setup modal close functionality
     * Note: Escape key is handled by explicit keydown listener on dialog
     * @private
     * @param {HTMLElement} closeButton
     */
    #setupModalClose(closeButton) {
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeZoom(), { signal: this.abortController.signal });
      }
    }

    /**
     * Setup scrollable thumbnail navigation
     * @private
     * @param {HTMLDialogElement} dialog
     */
    #setupThumbnailScrolling(dialog) {
      const container = dialog.querySelector('.thumbnails-image-box');
      const thumbnails = Array.from(container?.querySelectorAll('.image-thumbnail') || []);

      if (!container || thumbnails.length === 0) return;

      const firstThumb = thumbnails[0];
      const thumbnailHeight = firstThumb.getBoundingClientRect().height || 0;
      const thumbnailStyle = getComputedStyle(firstThumb);
      const margins = parseFloat(thumbnailStyle.marginTop) + parseFloat(thumbnailStyle.marginBottom);
      const itemHeight = thumbnailHeight + margins;

      container.style.scrollBehavior = 'smooth';
      container.style.height = `${itemHeight * 4}px`;

      container.insertAdjacentHTML('afterend', `
        <span class="up-arrow arrow" role="button" tabindex="0" aria-label="Previous images">
          ${window.theme?.elements?.svg_arrow_up || '▲'}
        </span>
        <span class="down-arrow arrow" role="button" tabindex="0" aria-label="Next images">
          ${window.theme?.elements?.svg_arrow_down || '▼'}
        </span>
      `);

      this.#setupScrollArrows(dialog, container, itemHeight);
      this.#scrollToActiveThumbnail(container, thumbnails, itemHeight);
    }

    /**
     * Setup scroll arrow functionality
     * @private
     * @param {HTMLDialogElement} dialog
     * @param {HTMLElement} container
     * @param {number} itemHeight
     */
    #setupScrollArrows(dialog, container, itemHeight) {
      const upArrow = dialog.querySelector('.up-arrow');
      const downArrow = dialog.querySelector('.down-arrow');

      if (!upArrow || !downArrow) return;

      const updateArrows = () => {
        const { scrollTop } = container;
        const maxScroll = container.scrollHeight - container.clientHeight;

        upArrow.style.display = scrollTop === 0 ? 'none' : 'inline';
        downArrow.style.display = scrollTop >= maxScroll ? 'none' : 'inline';
      };

      const scrollUp = () => {
        container.scrollTop -= itemHeight;
        updateArrows();
      };

      const scrollDown = () => {
        container.scrollTop += itemHeight;
        updateArrows();
      };

      const { signal } = this.abortController;

      upArrow.addEventListener('click', scrollUp, { signal });
      downArrow.addEventListener('click', scrollDown, { signal });

      const handleKeydown = (action) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          action();
        }
      };

      upArrow.addEventListener('keydown', handleKeydown(scrollUp), { signal });
      downArrow.addEventListener('keydown', handleKeydown(scrollDown), { signal });

      container.addEventListener('scroll', updateArrows, { signal });
      updateArrows();
    }

    /**
     * Auto-scroll to active thumbnail on modal open
     * @private
     * @param {HTMLElement} container
     * @param {Array<HTMLElement>} thumbnails
     * @param {number} itemHeight
     */
    #scrollToActiveThumbnail(container, thumbnails, itemHeight) {
      const activeIndex = thumbnails.findIndex(thumb =>
        thumb.querySelector('.thumbnail-active')
      );

      if (activeIndex >= 4) {
        container.scrollTop = (activeIndex - 3) * itemHeight;
      }
    }

    /**
     * Open mobile fullscreen zoom view
     * Uses native <dialog> element with showModal() for proper accessibility
     * @private
     */
    #openMobileModal() {
      const currentMedia = this.mediaItems[this.currentZoomIndex];

      // Store trigger element for focus return
      this.triggerElement = document.activeElement;

      // Use mobile-optimized zoom image (smaller than desktop)
      const zoomSrc = currentMedia.mobileZoom || currentMedia.zoom;

      // Create native dialog element
      const dialog = document.createElement('dialog');
      dialog.classList.add('zoom-container--mobile');
      dialog.setAttribute('aria-label', `${currentMedia.alt || this.productTitle} - Zoom view`);

      dialog.innerHTML = `
        <button class="product-media-modal__toggle"
                aria-label="Close zoom view"
                type="button">
          ${window.theme?.elements?.svg_close || '✕'}
        </button>
        <img src="${zoomSrc}"
             alt="${currentMedia.alt || this.productTitle}"
             width=""
             height="" />
      `;

      // Store abort controller signal for cleanup
      const { signal } = this.abortController;

      // Handle native close event (dialog.close())
      dialog.addEventListener('close', () => this.#onDialogClose(), { signal });

      // Handle Escape key explicitly - more reliable than native cancel event
      // which can be interfered with by document-level keydown listeners
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeZoom();
        }
      }, { signal });

      // Handle backdrop click
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          this.closeZoom();
        }
      }, { signal });

      document.body.appendChild(dialog);
      this.activeModal = dialog;

      // Show as modal
      dialog.showModal();

      // Lock body scroll to prevent scrolling behind the dialog
      window.scrollLock?.enabledBodyScrollLock();

      // Center the image
      const img = dialog.querySelector('img');
      if (img) {
        dialog.scrollLeft = (img.width - dialog.clientWidth) / 2;
        dialog.scrollTop = (img.height - dialog.clientHeight) / 2;
      }

      // Setup close button handler
      const closeButton = dialog.querySelector('.product-media-modal__toggle');
      closeButton?.addEventListener('click', () => this.closeZoom(), { signal });

      // Click on image also closes
      img?.addEventListener('click', () => this.closeZoom(), { signal });

      // Move focus to close button
      closeButton?.focus();
    }

    /**
     * Clean up zoom resources
     * @private
     */
    #cleanupZoom() {
      if (this.activeModal) {
        try {
          // Unlock body scroll before closing modal
          window.scrollLock?.disableBodyScrollLock();

          if (this.activeModal.open) {
            this.activeModal.close();
          }
          this.activeModal.remove();
        } catch (error) {
          console.warn('ProductMedia: Error during modal cleanup', error);
        }
      }

      this.triggerElement = null;
    }

    // ========================================
    // Original ProductMedia gallery methods
    // ========================================

    /**
     * Public method for parent components to update media
     * @param {string} mediaUrl - The URL to fetch new media content
     * @param {Object} options - Optional configuration
     */
    async updateMedia(mediaUrl, options = {}) {
      // Prevent unnecessary updates or concurrent updates
      if (!mediaUrl || mediaUrl === this.currentMediaUrl || this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      this.currentMediaUrl = mediaUrl;

      try {
        // Dispatch update start event
        this.dispatchEvent(new CustomEvent('product-media:update-start', {
          bubbles: true,
          detail: { mediaUrl, options }
        }));

        if (this.allowDebug()) {
          console.log(`🖼️ Product Media: Updating media`, mediaUrl);
        }

        // Apply loading state
        this.setLoadingState(true);

        // Fetch new media content
        const response = await fetch(mediaUrl);
        if (!response.ok) throw new Error(`Failed to fetch media: ${response.status}`);

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newMediaComponent = doc.querySelector('product-media');

        if (!newMediaComponent) {
          throw new Error('No product-media component found in response');
        }

        // Count images in the new gallery
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = newMediaComponent.innerHTML;
        const newImages = tempContainer.querySelectorAll('img');
        const isLargeGallery = newImages.length > 3;

        // Use enhanced transitions for large galleries, simple for small
        if (isLargeGallery) {
          await this.applyEnhancedTransition(newMediaComponent.innerHTML, newImages);
        } else {
          await this.applySimpleTransition(newMediaComponent.innerHTML);
        }

        // Dispatch success event
        this.dispatchEvent(new CustomEvent('product-media:update-success', {
          bubbles: true,
          detail: { mediaUrl, isLargeGallery, imageCount: newImages.length }
        }));

        if (this.allowDebug()) {
          console.log('✅ Product Media: Successfully updated media');
        }

      } catch (error) {
        console.error('❌ Product Media: Update failed:', error);

        // Dispatch error event
        this.dispatchEvent(new CustomEvent('product-media:update-error', {
          bubbles: true,
          detail: { mediaUrl, error: error.message }
        }));

      } finally {
        this.setLoadingState(false);
        this.isUpdating = false;
      }
    }

    /**
     * Simple delay helper
     * @private
     */
    #delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Simple, fast transition for small galleries
     */
    async applySimpleTransition(newContent) {
      this.style.transition = 'opacity 0.15s ease-out';
      this.style.opacity = '0.7';

      await this.#delay(75);

      this.innerHTML = newContent;
      this.reinitialize();
      this.style.opacity = '1';

      await this.#delay(150);

      this.style.transition = '';
      this.style.opacity = '';
    }

    /**
     * Enhanced transition with preloading for large galleries
     */
    async applyEnhancedTransition(newContent, images) {
      try {
        // Preload images for smoother transition
        if (images?.length > 1) {
          await this.preloadImages(images);
        }

        this.style.transition = 'opacity 0.3s ease-out';
        this.style.opacity = '0';

        await this.#delay(150);

        this.innerHTML = newContent;
        this.reinitialize();
        this.style.opacity = '1';

        await this.#delay(300);

        this.style.transition = '';
        this.style.opacity = '';
      } catch (error) {
        console.error('❌ Product Media: Enhanced transition error:', error);
        await this.applySimpleTransition(newContent);
      }
    }

    /**
     * Preload images for smoother transitions
     */
    async preloadImages(images) {
      const imagePromises = Array.from(images).map((img) => {
        return new Promise((resolve) => {
          const src = img.src || img.dataset.src;
          if (!src) {
            resolve();
            return;
          }

          const preloadImg = new Image();
          let resolved = false;

          const done = () => {
            if (!resolved) {
              resolved = true;
              resolve();
            }
          };

          preloadImg.onload = done;
          preloadImg.onerror = done;
          preloadImg.src = src;

          // Timeout to prevent hanging - clears itself after resolving
          setTimeout(done, 500);
        });
      });

      // Race between all images loading and a global timeout
      await Promise.race([
        Promise.all(imagePromises),
        this.#delay(400)
      ]).catch((error) => {
        console.warn('ProductMedia: Image preload interrupted or failed', error);
      });
    }

    /**
     * Set loading state visual feedback
     */
    setLoadingState(isLoading) {
      this.classList.toggle('product-media--loading', isLoading);
      this.style.opacity = isLoading ? '0.8' : '';
    }

    /**
     * Reinitialize component after content change
     */
    reinitialize() {
      // Update slides reference
      this.slides = this.querySelectorAll('.product__slide');

      // Re-add slide classes
      this.initializeSlides();

      // Reinitialize scroll effects
      this.initScrollEffect();

      // Reinitialize lazy loading if enabled
      if (this.dataset.lazyLoad === 'true') {
        // Clear previous lazy slides tracking
        this.lazySlides.clear();

        // Reinitialize observer for new lazy slides
        this.initLazyLoading();
      }
    }

    initScrollEffect() {
      // Check if viewport width is more than 767px and if isDesktopSlider is false
      if (window.innerWidth > 767 && !this.isDesktopSlider) {
        return; // exit the function. This means we are on a large screen and we do not want to render slider mode.
      }

      this.activeSlides = this.querySelectorAll(`.${this.cellSelector}`);
      let columnDivider = 2;
      let scrollContainer = window;

      if(this.activeSlides.length <= 0) {
        this.activeSlides = this.querySelectorAll(`.product__slide`);
        this.activeSlides.forEach((image) => image.classList.add('js-main-slide'));
      }

      if(this.closest('quick-add-modal')){
        columnDivider = 1;
        scrollContainer = this.closest('.quick-add-modal__content')
      }

      // add indicators
      const totalIndicators = Math.round(this.activeSlides.length / columnDivider);

      this.querySelector('.indicators')?.remove();
      if(totalIndicators > 1) {
        let indicatorsDiv = document.createElement('ul');
        indicatorsDiv.classList.add('indicators');

        for (let i = 1; i <= totalIndicators; i++) {
          indicatorsDiv.innerHTML += `<li class="${i === 1 ? 'current' : ''}"></li>`;
        }

        this.appendChild(indicatorsDiv);
      }

      // Clean up old scroll listeners
      if (this.scrollHandler) {
        const oldScrollContainer = this.lastScrollContainer || window;
        oldScrollContainer.removeEventListener('scroll', this.scrollHandler);
      }

      // Set up new scroll listener
      this.currentPage = 1;
      this.lastScrollContainer = scrollContainer;

      // Simple throttled scroll handler for better performance
      let scrollTimeout;
      this.scrollHandler = () => {
        if (scrollTimeout) return;

        scrollTimeout = setTimeout(() => {
          this.handleScrollUpdate(scrollContainer);
          scrollTimeout = null;
        }, 16); // ~60fps for smooth updates
      };

      scrollContainer.addEventListener('scroll', this.scrollHandler);
    }

    handleScrollUpdate(scrollContainer) {
      let scrollPos = scrollContainer.pageYOffset;
      let found = false;
      this.activeSlides.forEach((slide) => {
        if(!found && slide.offsetTop >= scrollPos) {
          found = true;
          this.activeSlides.forEach((__slide, index) => {
            __slide.classList.remove('currentpage');
            if(__slide === slide) {
              this.currentPage = Math.round((index + 1) / 2); // Assuming columnDivider = 2
              slide.classList.add('currentpage');
            }
          });

          this.updateIndicators();
        }
      });
    }

    updateIndicators() {
      let indicatorsLi = this.querySelectorAll('.indicators li');

      // remove current class from previous page
      indicatorsLi?.forEach((li) => li.classList.remove('current'));

      // add current class to current page
      indicatorsLi[this.currentPage - 1]?.classList.add('current');
    }

    /**
     * Cleanup when component is removed
     */
    disconnectedCallback() {
      // Abort all event listeners
      this.abortController.abort();

      // Clean up scroll handler
      if (this.scrollHandler && this.lastScrollContainer) {
        this.lastScrollContainer.removeEventListener('scroll', this.scrollHandler);
      }

      // Cleanup intersection observer
      if (this.observer) {
        this.observer.disconnect();
      }

      // Clean up zoom resources
      this.#cleanupZoom();
    }

    /**
     * Initialize lazy loading with Intersection Observer
     */
    initLazyLoading() {
      // Disconnect existing observer if present
      if (this.observer) {
        this.observer.disconnect();
      }

      const lazySlides = this.querySelectorAll('.js-lazy-slide');

      if (lazySlides.length === 0) return;

      // Create observer with a margin to start loading before slide is visible
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.10 && !this.lazySlides.has(entry.target)) {
            this.loadLazySlide(entry.target);
            this.lazySlides.add(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '0px',
        threshold: 0.10
      });

      // Observe all lazy slides
      lazySlides.forEach(slide => this.observer.observe(slide));

      if (this.allowDebug()) {
        console.log(`🖼️ Product Media: Initialized lazy loading for ${lazySlides.length} slides`);
      }
    }

    /**
     * Load a lazy slide's content by extracting from its <template> element
     * The template contains server-rendered HTML, so we just clone and inject it
     */
    loadLazySlide(slide) {
      // Prevent re-loading if slide is already loaded or loading
      if (!slide.classList.contains('js-lazy-slide') || slide.dataset.loading === 'true') {
        return;
      }

      // Find the template element containing the pre-rendered content
      const template = slide.querySelector('template');
      if (!template) {
        if (this.allowDebug()) {
          console.warn('🖼️ Product Media: No template found in lazy slide');
        }
        return;
      }

      // Mark as loading to prevent duplicate loads
      slide.dataset.loading = 'true';

      if (this.allowDebug()) {
        console.log(`🖼️ Product Media: Loading lazy slide ${slide.dataset.mediaIndex}`);
      }

      // Add lazy-loading class - media starts hidden, fades in when image loads
      slide.classList.add('lazy-loading');

      // Clone the template content and inject it into the slide
      const content = template.content.cloneNode(true);
      const img = content.querySelector('img');

      // Helper to complete the transition
      const completeTransition = () => {
        requestAnimationFrame(() => {
          slide.classList.remove('lazy-loading');
          slide.classList.add('lazy-loaded');
        });
      };

      if (img) {
        img.onload = completeTransition;

        // Handle already-cached images
        if (img.complete && img.naturalHeight !== 0) {
          completeTransition();
        }
      } else {
        completeTransition();
      }

      // Remove template and inject content
      // CSS :has(.media) handles clearing the skeleton background
      template.remove();
      slide.appendChild(content);

      // Update slide state
      slide.classList.remove('js-lazy-slide');
      slide.dataset.loaded = 'true';
      delete slide.dataset.loading;
    }

    /**
     * Debug helper
     */
    allowDebug() {
      return this.classList.contains('debug');
    }
  }

  customElements.define('product-media', ProductMedia);
}
